import { findVenueProductForStockTakeProduct } from './stockTakeConversions'
import { stockInStockWasEntered } from './stockOnHand'

export const BELOW_PAR_SO_FAR_LABEL = 'Below par so far'
/** Default hint — keep aligned with Order-to-Par (counted lines + shopping-list par). */
export const BELOW_PAR_SO_FAR_HINT =
  'Entered lines short of par — same product count Order to Par will suggest.'
export const STOCK_TAKE_BELOW_PAR_FILTER_LABEL = 'Below par'
export const STOCK_TAKE_BELOW_PAR_EMPTY =
  'None of these counted lines are below par.'

export const isEnteredStockTakeLine = line => stockInStockWasEntered(line)

const parQuantity = value => {
  const parsed = Number(value)
  if (!Number.isFinite(parsed) || parsed <= 0) return 0
  return parsed
}

/**
 * Shopping-list par only (same basis as Order to Par). Inactive products are
 * ignored so the HUD does not count lines OTP will refuse to order.
 */
export const resolveParLevelForStockLine = (line, venueProducts = []) => {
  const matched = findVenueProductForStockTakeProduct(line, venueProducts)
  if (!matched || matched.isActive === false) return 0
  return parQuantity(matched.parLevel)
}

export const formatBelowParUnits = value => {
  const n = Number(value) || 0
  if (Number.isInteger(n)) return String(n)
  return n.toFixed(2).replace(/\.?0+$/, '')
}

/**
 * Gap to par for one line. Uncounted zeros (including seeded blanks) are 0.
 * Dashboard In stock is stored in base / order units.
 */
export const belowParGapForLine = (line, venueProducts = []) => {
  if (!isEnteredStockTakeLine(line)) return 0
  const parLevel = resolveParLevelForStockLine(line, venueProducts)
  if (parLevel <= 0) return 0
  const currentStock = Number(line.inStock)
  if (!Number.isFinite(currentStock)) return 0
  const gap = parLevel - currentStock
  return gap > 0 ? gap : 0
}

export const summarizeBelowParSoFar = (lines = [], venueProducts = []) => {
  const list = Array.isArray(lines) ? lines : []
  let belowParUnits = 0
  let belowParLines = 0
  let enteredCount = 0

  list.forEach(line => {
    if (isEnteredStockTakeLine(line)) enteredCount += 1
    const gap = belowParGapForLine(line, venueProducts)
    if (gap > 0) {
      belowParUnits += gap
      belowParLines += 1
    }
  })

  const unitsLabel = formatBelowParUnits(belowParUnits)
  const hint =
    belowParLines > 0 && belowParUnits > 0
      ? `${belowParLines} product${
          belowParLines === 1 ? '' : 's'
        } · ${unitsLabel} units short — same basis as Order to Par.`
      : BELOW_PAR_SO_FAR_HINT

  return {
    enteredCount,
    belowParLines,
    belowParUnits,
    label: BELOW_PAR_SO_FAR_LABEL,
    hint,
    // Product/line count (not raw units) so the HUD matches OTP “Below par on N”.
    text: `${BELOW_PAR_SO_FAR_LABEL}: ${belowParLines}`,
  }
}

export const isBelowParCountedLine = (line, venueProducts = []) =>
  belowParGapForLine(line, venueProducts) > 0

/** Counted lines with par − stock > 0, biggest gap first. */
export const sortBelowParCountedLines = (lines = [], venueProducts = []) =>
  (Array.isArray(lines) ? lines : [])
    .filter(line => isBelowParCountedLine(line, venueProducts))
    .sort((left, right) => {
      const gapDiff =
        belowParGapForLine(right, venueProducts) -
        belowParGapForLine(left, venueProducts)
      if (gapDiff !== 0) return gapDiff
      return String(left?.title || '').localeCompare(String(right?.title || ''))
    })
